<template>
  <div class="c-reporting-form">
    <v-form>
      <v-container fluid>
        <v-row>
          <v-col cols="12" md="6" sm="6" xs="auto" class="pl-6 py-3">
            <v-row>
              <h2><b>{{ report.Project_Description }}</b></h2>
            </v-row>
            <v-row>
              {{ report.Task_Name }}
            </v-row>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" md="4" sm="4" xs="auto">
            <v-row>
              <v-col cols="auto">
                <div><b>{{ currentReport.Estimate | valToFixed }}</b></div>
                <div><b>{{ totalReported | valToFixed }}</b></div>
                <div><b>{{ totalPaid | valToFixed }}</b></div>
              </v-col>
              <v-col>
                <div><b>Estimated</b></div>
                <div><b>&#8721; Total Reported</b></div>
                <div><b>&#8721; Total Paid</b></div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4">
            <v-select
              v-model="currentReport.Activity_Type"
              :items="activityTypes"
              item-text="Description"
              item-value="Activity_Type"
              label="Activity Type"
              @change="changed = true"
            ></v-select>
          </v-col>
          <v-col cols="4">
            <TimePicker
              v-model="currentReport.Real_Dur"
              label="Reported Duration"
              @change="changed = true"
            />
          </v-col>
          <v-col cols="4">
            <TimePicker
              v-model="currentReport.Report_Dur"
              label="Paid Duration"
              @change="changed = true"
            />
          </v-col>
          <v-col cols="12" class="description-col">
            <div class="description-wrapper">
              <DescriptionWithSpellCheck
                :key="'update-textfield-' + currentReport.Task_ID + '-' + updateTextField"
                label="Description"
                v-model="currentReport.Description"
                @change="descriptionChanged"
              />

              <BadWords
                :description="currentReport.Description"
                :hours="currentReport.Real_Dur"
                :bad-words="badWords"
              />
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-spacer></v-spacer>
          <v-col cols="12" md="auto" sm="auto"
            v-show="currentReport.Reporting_ID"
          >
            <v-btn
              small
              width="6.5em"
              color="red darken-2 white--text"
              @click="toggleDeleteReporting()"
            >
              Delete
            </v-btn>
          </v-col>
          <v-col cols="12" md="auto" sm="auto">
            <v-btn
              :disabled="!changed"
              small
              color="primary"
              width="6.5em"
              @click="saveReporting()"
            >
              Save
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <ConfirmOrDeniedDialog
      v-if="showDeleteReportingDialog"
      v-model="showDeleteReportingDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      @onCancelClick="showDeleteReportingDialog = false"
      @onDeleteClick="deleteReporting"
    />
  </div>
</template>

<script>
import { sharedReportingMixin } from '@/mixins/sharedReportingMixin'
import { getApiErrorMessage } from '@/utils/apiError'

export default {
  name: 'ReportingForm',
  mixins: [sharedReportingMixin],
  props: {
    report: Object,
    date: String
  },
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue'),
    DescriptionWithSpellCheck: () => import('@/components/reporting/DescriptionWithSpellCheck.vue'),
    TimePicker: () => import('@/components/global/TimePicker.vue'),
    BadWords: () => import('@/components/global/BadWords.vue')
  },
  data () {
    return {
      showDeleteReportingDialog: false,
      totalReported: 0,
      totalPaid: 0,
      changed: false,
      updateTextField: 0,
      realDuration: false,
      reportDuration: false,
      currentReport: {},
      badWords: [],
      defaultReportingRanges: [
        { formattedTime: '0:30' },
        { formattedTime: '1:00' },
        { formattedTime: '1:30' },
        { formattedTime: '2:00' },
        { formattedTime: '2:30' },
        { formattedTime: '3:00' },
        { formattedTime: '3:30' },
        { formattedTime: '4:00' },
        { formattedTime: '4:30' },
        { formattedTime: '5:00' },
        { formattedTime: '5:30' },
        { formattedTime: '6:00' },
        { formattedTime: '6:30' },
        { formattedTime: '7:00' },
        { formattedTime: '7:30' },
        { formattedTime: '8:00' },
        { formattedTime: '10:00' },
        { formattedTime: '12:00' },
        { formattedTime: '14:00' },
        { formattedTime: '16:00' },
        { formattedTime: '18:00' },
        { formattedTime: '20:00' },
        { formattedTime: '23:00' }
      ]
    }
  },
  mounted () {
    if (!this.report.Activity_Type) {
      this.report.Activity_Type = this.settings.defaultActivityType
    }
    if (!this.report.Description) this.report.Description = ''
    this.currentReport = {}
    for (const key in this.report) {
      this.$set(this.currentReport, key, this.report[key])
    }
    if (this.currentReport.Task_ID) {
      this.getTotalRepPaid()
    }
    this.fetchBadWords()
  },
  computed: {
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    activityTypes () {
      return this.$store.getters.getActivityTypes
    },
    user () {
      return this.$store.getters.getUser
    },
    settings () {
      return this.$store.getters.getSettings
    },
    employeeRoles () {
      return this.$store.getters.getEmployeeRoles
    }
  },
  filters: {
    valToFixed (val) {
      const res = +val
      if (res) {
        if ((Math.ceil(res * 100) % 10) !== 0) return res.toFixed(2)
        if ((Math.ceil(res * 100) % 100) !== 0) return res.toFixed(1)
        return res.toFixed(0)
      }
      return 0
    }
  },
  methods: {
    async fetchBadWords () {
      this.badWords = await this.getBadWords()
    },
    convertDurationToHours (duration) {
      return this.convertTimeToHours(duration)
    },
    canReport (projectName) {
      const excludedProjects = ['DC Vacations', 'DC Sick-Leave']
      const isFinancier = this.employeeRoles.financier.val

      if (!isFinancier && excludedProjects.includes(projectName)) {
        this.$store.dispatch('setError', { errorMsg: 'Only Financier role can Save/Update this project report' })
        return false
      }
      return true
    },
    getTotalRepPaid () {
      this.axios.get(`/api/totals-by-task/${this.currentReport.Task_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.totalPaid = response.data[0].Total_Paid_Dur
        this.totalReported = response.data[0].Total_Report_Dur
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    descriptionChanged (e) {
      this.changed = true
      this.$emit('changed', true)
    },
    allowedMinutes (m) {
      return m === 30 || m === 0
    },
    setRealDur () {
      this.$refs.realDuration.save(this.currentReport.Real_Dur)
      if (!this.currentReport.Report_Dur) {
        this.currentReport.Report_Dur = this.currentReport.Real_Dur
      }
      this.changed = true
      this.$emit('changed', true)
    },
    setReportDur () {
      this.$refs.reportDuration.save(this.currentReport.Report_Dur)
      if (!this.currentReport.Real_Dur) {
        this.currentReport.Real_Dur = this.currentReport.Report_Dur
      }
      this.changed = true
      this.$emit('changed', true)
    },
    checkForm () {
      return !!this.currentReport.Real_Dur && !!this.currentReport.Report_Dur && !!this.currentReport.Description && !!this.currentReport.Activity_Type
    },
    saveReporting () {
      if (this.checkForm()) {
        if ((this.currentReport.Reporting_ID) && this.canReport(this.currentReport.Project_Description)) this.updateReporting()
        else if (this.canReport(this.currentReport.Project_Description)) {
          this.currentReport.Start_Date = this.date
          this.insertReporting()
        }
      } else {
        this.$store.dispatch('setError', { errorMsg: 'Please fill all fields' })
      }
    },
    toggleDeleteReporting () {
      this.showDeleteReportingDialog = true
    },
    deleteReporting () {
      this.axios.delete('/api/repbydate/' + this.currentReport.Reporting_ID, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.currentReport = response.data[0]
        this.currentReport.Activity_Type = this.activityTypes[this.defaultActivityTypeIndex]
        this.changed = false
        this.$emit('changed', false)
        this.$emit('updateReports')
        this.$store.dispatch('setSuccess', { successMsg: 'Reporting successfully deleted' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateReporting () {
      this.axios.put('/api/repbydate/' + this.currentReport.Reporting_ID, this.currentReport, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.currentReport = Object.assign({}, this.currentReport, response.data[0])
        this.changed = false
        this.$emit('changed', false)
        this.$emit('updateReports')
        this.$store.dispatch('setSuccess', { successMsg: 'Reporting successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    insertReporting () {
      this.axios.post('/api/repbydate/', this.currentReport, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.currentReport = Object.assign({}, this.currentReport, response.data[0])
        this.changed = false
        this.$emit('changed', false)
        this.$emit('updateReports')
        this.$store.dispatch('setSuccess', { successMsg: 'Reporting successfully saved' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  },
  watch: {
    'currentReport.Real_Dur': function (newVal, oldVal) {
      if (newVal && this.currentReport.Report_Dur === null) {
        this.currentReport.Report_Dur = this.currentReport.Real_Dur
      }
    }
  }
}
</script>

<style lang="scss">
.c-reporting-form {
  .description-col {
    .description-wrapper {
      position: relative;
    }

    .bad-words-indicator {
      position: absolute;
      top: 10px;
      right: 10px;
      z-index: 2;
    }
  }
}
</style>
